import { Route, RouteConfig } from "vue-router";
import {
  LAUNCH_ROUTES_PATHS_V2,
  launchRoutesV2,
} from "./launch/v2/launch-routes-v2";
import { makeLoginGuard } from "./router/guards";
import scoreboardRoutes from "./competition/scoreboard/scoreboard-routes";
import rs4Routes from "./competition/scoreboard/rs4/r4s-routes";
import { useWordPressLogin } from "@/auth/auth-constants";
import { CONFIG } from "@/common/config";

const isLoggedInGuardV2 = makeLoginGuard(LAUNCH_ROUTES_PATHS_V2.LOGIN_V2);

function makeWordPressGuard(
  wpPath: string,
  queryParams?: Record<string, string>,
) {
  return (to: Route, from: Route, next: any) => {
    if (useWordPressLogin()) {
      const baseUrl = CONFIG.E4S_HOST || window.location.origin;
      let url = baseUrl + wpPath;
      if (queryParams) {
        const params = new URLSearchParams(queryParams);
        url += (url.includes("?") ? "&" : "?") + params.toString();
      }
      window.location.href = url;
      return;
    }
    next();
  };
}

export const rootRoutes: RouteConfig[] = [
  {
    name: "router-ping",
    path: "/__router-ping",
    component: {
      render(h: any) {
        return h(
          "div",
          {
            attrs: { id: "router-ping" },
            style: {
              padding: "16px",
              background: "#fffae6",
              border: "1px solid #f0c36d",
              fontFamily: "Arial, sans-serif",
            },
          },
          ["ROUTER PING: rendered"],
        );
      },
    },
    meta: { title: "Router Ping" },
  },
  {
    name: "home",
    path: "",
    redirect: "/home",
  },
  {
    name: "showentries",
    path: "/showentries",
    redirect: "/home",
  },
  {
    path: "/v2",
    component: () => import("./launch/v2/launch-v2.vue"),
    beforeEnter: (to: Route, from: Route, next: any) => {
      console.warn(
        "[V2 Route Transition] Accessing /v2/ route:",
        to.fullPath,
        "- Consider updating to root path",
      );
      next();
    },
    children: launchRoutesV2,
  },

  // --- Root routes with V2 layout wrapper ---
  {
    path: "/home",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.HOME_V2,
        path: "",
        component: () => import("./public/entry-public/v2/entry-public-v2.vue"),
        meta: { title: "Home" },
      },
    ],
  },
  {
    path: "/entry/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.ENTRY_V2,
        path: "",
        component: () => import("./entry/v2/entry-route-v2.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Entry",
        },
      },
    ],
  },
  {
    path: "/entry-v3/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.ENTRY_V3,
        path: "",
        component: () => import("./entry/v3/entry-route-v3.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Entry V3",
        },
      },
    ],
  },
  {
    path: "/entry",
    redirect: (to: any) => {
      return to.query.compid ? `/entry/${to.query.compid}` : "/home";
    },
  },
  {
    path: "/show-entry/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.SHOW_ENTRY_v2,
        path: "",
        component: () => import("./public/entry-public/v2/entry-direct-v2.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Comp",
        },
      },
    ],
  },
  {
    path: "/athlete/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.ATHLETE_V2,
        path: "",
        component: () => import("./athlete/v2/form/athlete-form-route-v2.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Athlete",
        },
      },
    ],
  },
  {
    path: "/athlete-pb/:entryId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.ATHLETE_PB_V2,
        path: "",
        component: () => import("./athleteCompSched/pb/v3/AthletePbRoute.vue"),
        meta: {
          title: (params: { entryId: string }) =>
            "Entry Id" + params.entryId + ": PB",
        },
      },
    ],
  },
  {
    path: "/athletes",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.ATHLETES_V2,
        path: "",
        component: () =>
          import("./athlete/v2/search/athlete-search-route-v2.vue"),
        meta: { title: "Athletes" },
      },
    ],
  },
  {
    path: "/schedule/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.SCHED_COMP_PUBLIC,
        path: "",
        component: () =>
          import(
            "./competition/results/public/schedule/v2/schedule-comp-public-route-v2.vue"
          ),
        meta: { title: "Schedule" },
      },
    ],
  },
  {
    path: "/messages/:compId?",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.MESSAGES_V2,
        path: "",
        component: () =>
          import("./email-message/grid/v2/email-message-grid-route-v2.vue"),
        meta: {
          title: (params: { compId: number }) =>
            "Messages" +
            (params.compId && params.compId > 0
              ? " for Comp: " + params.compId
              : ""),
        },
      },
    ],
  },
  {
    path: "/user-search",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.USER_SEARCH_V2,
        path: "",
        component: () => import("./admin/user/v2/user-search-v2.vue"),
        meta: { title: "Users" },
      },
    ],
  },
  {
    path: "/user/:id?",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.USER_V2,
        path: "",
        component: () => import("./admin/user/v2/user-route-container-v2.vue"),
        meta: { title: "User" },
      },
    ],
  },
  {
    path: "/builder-choice",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.BUILDER_CHOICE_V2,
        path: "",
        component: () => import("./builder/form/builder-choice.vue"),
        meta: { title: "Create Competition" },
      },
    ],
  },
  {
    path: "/builder/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.BUILDER_V2,
        path: "",
        component: () => import("./builder/form/builder-form.vue"),
        meta: {
          title: (params: { id: number }) => params.id + ": Builder",
        },
      },
    ],
  },
  {
    path: "/stepper-builder/:id?",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.STEPPER_BUILDER_V2,
        path: "",
        component: () =>
          import("./builder/form/stepper/stepper-builder-route.vue"),
        meta: { title: "Create Competition" },
      },
    ],
  },
  {
    path: "/form-generic-example",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.FORM_GENERIC_EXAMPLE_V2,
        path: "",
        component: () =>
          import("./common/ui/layoutV2/form/form-generic-example.vue"),
        meta: { title: "Example Form" },
      },
    ],
  },
  {
    path: "/contact-organiser/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.CONTACT_ORGANISER_V2,
        path: "",
        component: () =>
          import("./competition/askorganiser/AskOrganiserRoute.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Contact",
        },
      },
    ],
  },
  {
    path: "/club-search",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.CLUB_SEARCH_V2,
        path: "",
        component: () => import("./club/v2/club-search.vue"),
        meta: { title: "Clubs" },
      },
    ],
  },
  {
    path: "/club/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.CLUB_V2,
        path: "",
        component: () => import("./club/v2/club-route.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Club",
        },
      },
    ],
  },
  {
    path: "/org-search",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.ORG_SEARCH_V2,
        path: "",
        component: () => import("./org/ui/OrgsRoute.vue"),
        meta: { title: "Organisations" },
      },
    ],
  },
  {
    path: "/shop/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.SHOP_V2,
        path: "",
        component: () => import("./secondary/v2/secondary-public-v2.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Shop",
        },
      },
    ],
  },
  {
    path: "/shop-v3/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: "shop-v3",
        path: "",
        component: () => import("./secondary/v3/shop-route-v3.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Shop V3",
        },
      },
    ],
  },
  {
    path: "/checkin/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.CHECK_IN_V2,
        path: "",
        component: () =>
          import(
            "./competition/checkin/checkin-organiser/checkin-organiser.vue"
          ),
        meta: {
          title: (params: { id: string }) => params.id + ": Check In",
        },
      },
    ],
  },
  {
    path: "/ticket-admin/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.TICKET_ADMIN_V2,
        path: "",
        component: () =>
          import("./secondary/ticket/ticket-admin/ticket-admin-route.vue"),
        meta: {
          title: (params: { compId: string }) =>
            params.compId + ": Ticket Admin",
        },
      },
    ],
  },
  {
    path: "/ticket-form-gatekeeper/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.TICKET_FORM_GATEKEEPER,
        path: "",
        component: () =>
          import(
            "./secondary/ticket/ticket-gatekeeper/ticket-route-gatekeeper.vue"
          ),
        meta: {
          title: (params: { compId: string }) =>
            params.compId + ": Ticket Admin",
        },
      },
    ],
  },
  {
    path: "/ticket/:compId/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.TICKET_FORM_V2,
        path: "",
        component: () => import("./secondary/ticket/ticket-route.vue"),
        meta: {
          title: "Ticket",
        },
      },
    ],
  },
  {
    path: "/results/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.RESULTS_MANUAL,
        path: "",
        component: () =>
          import(
            "./competition/results/public/schedule/results-public-route.vue"
          ),
        meta: { title: "Results" },
      },
    ],
  },
  {
    path: "/results-entry/:compId/:eventGroupId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.RESULTS_ENTRY,
        path: "",
        component: () => import("./competition/results/result-route.vue"),
        meta: { title: "Results Entry" },
      },
    ],
  },
  {
    path: "/results-import-file/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.RESULTS_IMPORT_FILE,
        path: "",
        component: () =>
          import(
            "./competition/results/results-file-import/ResultsImportFileRoute.vue"
          ),
        meta: { title: "Photo Finish" },
      },
    ],
  },
  {
    path: "/results-import/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.RESULTS_IMPORT_V2,
        path: "",
        component: () =>
          import(
            "./competition/results/results-file-import/ResultsImportFileRoute.vue"
          ),
        meta: {
          title: (params: { compId: string }) =>
            params.compId + ": Results Import",
        },
      },
    ],
  },
  {
    path: "/scoreboard-output-list/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.SCOREBOARD_OUTPUT_LIST,
        path: "",
        component: () =>
          import(
            "./competition/scoreboard/scoreboard-output/list/scoreboard-output-list-route.vue"
          ),
        meta: {
          title: (params: { compId: string; outputId: string }) =>
            params.compId + "/" + params.outputId + ": Scoreboard Sched",
        },
      },
    ],
  },
  {
    path: "/bibs/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.BIBS_V2,
        path: "",
        component: () => import("./competition/admin/bibs/bib-form-route.vue"),
        meta: {
          title: (params: { id: string }) => params.id + ": Bibs",
        },
      },
    ],
  },
  {
    path: "/age-group-list/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.AGE_GROUP_LIST_V2,
        path: "",
        component: () =>
          import("./agegroup/V2/presentation/age-group-list-v2.vue"),
        meta: { title: "Age Groups" },
      },
    ],
  },
  {
    path: "/age-group-edit/:id",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.AGE_GROUP_EDIT_V2,
        path: "",
        component: () =>
          import("./agegroup/V2/presentation/age-group-edit-v2.vue"),
        meta: { title: "Edit Age Group" },
      },
    ],
  },
  {
    path: "/xc-upload/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.XC_UPLOAD_V3,
        path: "",
        component: () =>
          import("./competition/results/xc-upload/xc-upload-route.vue"),
        meta: { title: "Cross Country Upload" },
      },
    ],
  },
  {
    path: "/actions/:compId/:athleteId/:clubId/:entryId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.COMP_EVENT_ACTIONS_V2,
        path: "",
        component: () =>
          import(
            "./athleteCompSched/comp-event-actions/comp-event-actions-loader.vue"
          ),
        meta: { title: "Comp Actions" },
      },
    ],
  },
  {
    path: "/stripe-users",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.STRIPE_USERS_V2,
        path: "",
        component: () => import("./admin/stripe/StripeUsersWrapper.vue"),
        meta: { title: "Stripe Users" },
      },
    ],
  },
  {
    path: "/help-form-list",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.HELP_FORM_LIST_V2,
        path: "",
        component: () =>
          import("./common/ui/field/field-help/help-form-list.vue"),
        meta: { title: "Help Config" },
      },
    ],
  },
  {
    path: "/sec-grid",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.SECONDARY_GRID_V2,
        path: "",
        component: () => import("./secondary/config/secondary-form-grid.vue"),
        meta: { title: "Store Config" },
      },
    ],
  },
  {
    path: "/reports",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.REPORTS_V2,
        path: "",
        component: () => import("./admin/reports/reports-container.vue"),
        meta: { title: "Reports" },
      },
    ],
  },
  {
    path: "/waitlist/:compId",
    component: () => import("./launch/v2/root-layout-v2.vue"),
    beforeEnter: isLoggedInGuardV2,
    children: [
      {
        name: LAUNCH_ROUTES_PATHS_V2.WAITLIST_V2,
        path: "",
        component: () => import("./waitlist/WaitlistRoute.vue"),
        meta: {
          title: (params: { compId: string }) => params.compId + ": Waitlist",
        },
      },
    ],
  },

  // --- Auth routes (no layout wrapper - full page) ---
  {
    name: "login",
    path: "/login",
    component: () => import("./auth/v2/login-container-v2.vue"),
    beforeEnter: makeWordPressGuard("/wp-login.php"),
    meta: { title: "Login" },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.REGISTER_V2,
    path: "/register",
    component: () => import("./auth/v2/register/register-container-v2.vue"),
    beforeEnter: makeWordPressGuard("/wp-login.php", { action: "register" }),
    meta: { title: "Register" },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.FORGOT_V2,
    path: "/forgot",
    component: () => import("./auth/v2/forgot/forgot-container-v2.vue"),
    beforeEnter: (to: Route, from: Route, next: any) => {
      if (useWordPressLogin()) {
        window.location.href =
          (CONFIG.E4S_HOST || window.location.origin) +
          "/wp-login.php?action=lostpassword";
        return;
      }
      next();
    },
    meta: { title: "Forgot" },
  },
  {
    name: LAUNCH_ROUTES_PATHS_V2.RESET_V2,
    path: "/reset_pwd",
    component: () => import("./auth/v2/reset/reset-container-v2.vue"),
    beforeEnter: (to: Route, from: Route, next: any) => {
      if (useWordPressLogin()) {
        window.location.href =
          (CONFIG.E4S_HOST || window.location.origin) +
          "/wp-login.php?action=lostpassword";
        return;
      }
      next();
    },
    meta: { title: "Reset" },
  },

  // --- Other standalone routes ---
  {
    name: "scoreboard",
    path: "/scoreboard",
    component: () => import("./competition/scoreboard/scoreboard-section.vue"),
    children: scoreboardRoutes,
    meta: { title: "Scoreboard" },
  },
  {
    name: "r4s",
    path: "/r4s",
    component: () => import("./competition/scoreboard/rs4/r4s-section.vue"),
    children: rs4Routes,
    meta: { title: "Results 2" },
  },
  {
    name: "toast-test",
    path: "/toast-test",
    component: () => import("./test/toast-test.vue"),
    meta: { title: "Toast Test" },
  },
  {
    name: "sentry-test",
    path: "/sentry-test",
    component: () => import("./test/sentry-test.vue"),
    meta: { title: "Sentry Test" },
  },
  {
    name: "mobile-test-v2",
    path: "/mobile-test-v2",
    component: () => import("./test/mobile-test-v2.vue"),
    meta: { title: "Mobile Test V2" },
  },
];
